import { useSyncExternalStore } from 'react';
import type { Capabilities } from '../api/meta';
import type { AssignedDocRef, AssignedNodeRef, FavoriteRef, RecentRef } from '../api/me';
import type { MyTodo } from '../api/todos';
import type { PresenceUser } from '../features/presence/PresenceStack';
import type { AppTarget } from '../features/targets/TargetsScreen';
import type { GroundingDefaults } from '../lib/aiFields';
import type { BootPhase } from '../store/slices/boot';
import { mirrorAppSnapshot } from '../store/fromApp';
import { undoLabel } from './undo';
import { getRoster } from './presence';
import type {
  CrossLink,
  DocId,
  DocType,
  Edges,
  MapNode,
  NodeId,
  NodeMap,
  Permissions,
  PipelineStage,
  SearchScope,
  TagEdges,
  TagMap,
  WorkflowState,
} from '../lib/types';
import type { AdminScreen, ParsedLocation, PermissionTab, SearchDocKind } from '../lib/location';
import type { NodeHealthCounts } from '../lib/health';

/* ---------------------------------------------------- the documentation workflow (V0.38) */

/**
 * One doc page's workflow standing, at its RESOLVED locale — `GET /api/docs/workflows`
 * resolves the requested → source-locale fallback server-side and says which locale each
 * entry is about, so the doc bar and the bulk map cannot disagree about which page a reader
 * is looking at.
 *
 * `reviewedAt` is null both when nobody has cleared the page and immediately after a content
 * save demoted it, because a demotion CLEARS the stamp: leaving a name on text nobody has
 * read since would read as an endorsement of it.
 */
export interface DocWorkflowEntry {
  workflowState: WorkflowState;
  /** The RESOLVED locale this entry is about — the current one, or the source fallback. */
  locale: string;
  /** True when the requested locale had no row and this is the source-locale stand-in. */
  fallback: boolean;
  /** ISO timestamp, or null. */
  reviewedAt: string | null;
  /**
   * The ELIGIBLE doc responsibles for this page AT THE ENTRY'S LOCALE — groups already
   * expanded to their eligible members server-side, which is what lets the mirror answer
   * team-borne assignment with zero group state. Doc responsibles ride this fetch and never
   * the map payload — the accepted, documented latency (plan trap #6). Preserved across
   * `noteDocWorkflow()` patches, whose transition responses do not carry them.
   */
  responsibles: string[];
  /** The GROUPS directly assigned at that locale — display-only ids, members folded above. */
  responsibleGroups: string[];
}

/**
 * One row of `GET /api/users/directory` — the only non-admin way to turn a user id into a
 * name. FOUR FIELDS AND NO MORE: it is a real privacy widening and is recorded as one in
 * `PRIVACY.md` §1, so anything else wanted here is a change to that document first. (V0.37
 * replaced the single `canEdit` flag with the two eligibility flags — what a picker actually
 * asks is "who CAN be assigned here".)
 */
export interface DirectoryUser {
  id: string;
  displayName: string;
  /** Whether that account's role (own, or granted by their group) carries `NODE_RESPONSIBLE` (macro-expanded). */
  canBeNodeResponsible: boolean;
  /** Whether that account's role (own, or granted by their group) carries `DOC_RESPONSIBLE` (macro-expanded). */
  canBeDocResponsible: boolean;
}

/**
 * One row of `GET /api/users/directory/groups` (V0.37.1) — the group-shaped sibling of
 * `DirectoryUser` above, same privacy posture, same gate. `memberIds` is what lets the picker
 * expand a group into its member chips without a second round trip.
 */
export interface DirectoryGroup {
  id: string;
  name: string;
  memberIds: string[];
  /** Whether at least one CURRENT member carries `NODE_RESPONSIBLE` — assigning a group with
   * none would be a silent no-op, so the server refuses it and the picker should not offer it. */
  canBeNodeResponsible: boolean;
  /** The same fact for `DOC_RESPONSIBLE` (V0.38) — what the doc-flavored picker filters on. */
  canBeDocResponsible: boolean;
}

/**
 * What the one dismissible banner (`#routenotice`) is currently saying.
 *
 * Two of these are the unreachable-link wordings (plan 1.6) — which one is a permission fact
 * rather than a deletion fact. Two more are concurrency (plan Part 3): the map was quietly
 * refreshed under this user because somebody else changed it, or one of THEIR OWN changes was
 * refused because somebody else saved first. One banner, several wordings, one set of ids — a
 * second notice implementation is the failure mode this codebase has already paid for once.
 */
export type RouteNoticeKind =
  | 'not-permitted'
  | 'not-found'
  | 'map-refreshed'
  | 'edit-rejected'
  /** One of this user's changes was refused for a MISSING PERMISSION (V0.37) — a race between
   *  an affordance already drawn and a brick withdrawn behind it. `noticeDetail` names the
   *  missing bricks the server reported. */
  | 'edit-forbidden'
  /** Realign was refused because this user's map is a SUBSET (V0.43). The canonical layout
   *  pass is published to everybody, and a tree with branches withheld would lay the visible
   *  siblings out over the gap and persist that. See `mapFiltered`. */
  | 'realign-partial'
  /** The SESSION ended (V0.45) — a 401 came back from any route, so the login card is up and
   *  this says why it is. `noticeDetail` names the unsent edits when there were any. Without
   *  it an expired session was a silently empty screen, which is the whole defect. */
  | 'session-ended'
  /** An administrator is RESTORING A BACKUP (V0.48), so every route is answering 503 for a
   *  minute or two. The only notice that clears ITSELF on a fact rather than a timer: the
   *  server coming back is what ends it (`MapSync.noteServerBack`), because "ask again in a
   *  moment" is a promise and a banner that expired before the promise was kept would leave
   *  the reader clicking into failures with no explanation. */
  | 'maintenance';

/**
 * THE APP'S STATE, and the read model every component sees.
 *
 * This is the successor to `infostation.html`'s top-level variables and to the bridge that used
 * to carry them across. There is one source of truth now and it is here.
 *
 * ── WHY A MUTABLE OBJECT AND A PUBLISHED SNAPSHOT ────────────────────────────────────────
 * Two properties have to hold at once, and neither is negotiable:
 *
 *  - The map is mutated in place and often. A drag moves a subtree on every `pointermove`; a
 *    reparent rewrites colour and depth across it. Rebuilding an immutable tree per frame is
 *    exactly what `REACT-REWRITE-PLAN.md` says not to do, and the Zustand store exists so the
 *    hot path can read and commit without hooks at all (`store/`, `features/map/`).
 *  - Everything that is NOT the map wants an ordinary React read: a flat, cached object that
 *    changes identity when something changed. That is `AppSnapshot`, delivered through
 *    `useSyncExternalStore`.
 *
 * So `S` holds the state, and `publish()` is the one place that says it moved: it rebuilds the
 * snapshot, mirrors it into the store, and notifies subscribers. Every function that changes
 * state ends in `publish()` — under the names it has always had, `render()` and `renderTags()`
 * and `drawWires()`, because about fifty call sites say those and every one of them means "the
 * state moved, show it".
 *
 * ── THE CONTAINERS ARE REPLACED, THE OBJECTS ARE NOT ─────────────────────────────────────
 * `snapshot()` hands out `{...S.nodes}`, not `S.nodes`. The node and tag OBJECTS inside are the
 * live ones — reading `s.nodes[id].label` is always current and costs nothing — but the maps
 * holding them are copied on every publish. That is load-bearing, not caution: the app mutates
 * `nodes`, `edges`, `tags`, `tagEdges` and `links` in place, so handing the originals across
 * would give React objects whose identity never changes however much their contents do. Every
 * `useMemo` keyed on them would be permanent, and `lib/graph.ts`'s `buildGraph()` — which caches
 * on the identity of `edges` — would serve a stale forward-adjacency index after any reparent.
 * That was a real bug once, found through the top bar's expand/collapse toggle silently
 * refusing to update, and fixed here rather than patched per consumer.
 *
 * ── THE SNAPSHOT IS CACHED, AND THAT IS NOT AN OPTIMISATION ──────────────────────────────
 * `useSyncExternalStore` compares successive `getSnapshot()` results by identity and throws
 * "The result of getSnapshot should be cached" — then loops — if a fresh object comes back every
 * time. The cache is invalidated by `publish()`, which is exactly when the state really changed.
 */

/* ------------------------------------------------------------------ what a session is */

/** The signed-in account, as `GET /api/auth/me` reported it. */
export interface CurrentUser {
  id: string;
  username: string;
  displayName?: string;
  /** This account's avatar version, null for none — the token, never the bytes. */
  avatarV?: number | null;
  /**
   * This account's own team, or null (V0.50). Optional because a server that predates it sends
   * nothing, and every reader treats absent as "no team" — which is the safe direction: a
   * to-do checkbox draws disabled rather than offering a tick the server would refuse.
   */
  groupId?: string | null;
}

/** One entry of the language switcher. */
export interface Language {
  code: string;
  name: string;
}

/* ------------------------------------------------------------------------- the state */

export interface AppCoreState {
  /* ---- the map ---- */
  nodes: NodeMap;
  /** INVERTED, as it has always been: `edges[childId] = parentId`. */
  edges: Edges;
  links: CrossLink[];
  tags: TagMap;
  tagEdges: TagEdges;
  /** Local counters, used only to cycle colours for new nodes and tags. Ids are server-assigned. */
  nid: number;
  tid: number;
  /** Optimistic-concurrency token for `PUT /api/map`. */
  dataVersion: number;
  /**
   * Did the server WITHHOLD anything from the map it just sent (V0.43)?
   *
   * `GET /api/map` omits nodes this user may not see, so the client's tree can be a subset of
   * the real one. That is fine for reading and fine for dragging what you can see, but not for
   * the canonical layout pass, whose output is persisted for everybody: laid out over a tree
   * with branches missing, the visible siblings close the gap and that becomes the shared
   * truth. `features/map/mapController.ts` reads this to decide whether a structural pass may
   * publish, and `realign()` reads it to refuse rather than to lie.
   *
   * Reported by the server rather than inferred, because it cannot be inferred: a node hidden
   * by a TAG leaves `hiddenNodeSet` empty while still shrinking the tree.
   */
  mapFiltered: boolean;

  /* ---- documents ---- */
  docTitles: Record<string, string>;
  docAiGenerated: Record<string, boolean>;
  /** Only the exceptions are stored; an id that is absent is a `standard` page. */
  docTypes: Record<string, DocType>;
  docUrls: Record<string, string>;
  /**
   * Doc-page id -> its frozen slug, from `/api/map` — the stable identifier deep links are
   * built from (`/{locale}/doc/{slug}`). The router keeps the reverse map (plan 1.6).
   */
  docSlugs: Record<string, string>;
  /**
   * Doc-page id -> true when its address no longer follows its title (V0.45.1) — the "!" on the
   * doc bar's re-slug button.
   *
   * SPARSE: only the drifted pages are listed, so the common case costs nothing. DECIDED BY
   * THE SERVER, because the rule compares against the title the slug was NAMED BY (English,
   * then the default locale) and this client is only ever sent one locale's title — comparing
   * against that would light the mark up on every page the moment somebody switched to French.
   * The equivalent for nodes rides the node itself as `slugDrifted`, since the popup has it.
   */
  docSlugDrift: Record<string, true>;
  /**
   * Former addresses that still resolve, slug -> id, for both kinds (V0.45.1). The router's
   * reverse lookups consult these AFTER the live slugs, so a link somebody shared before a
   * re-slug still opens the right thing, and a live address always wins. Omitted from the
   * payload when there are none.
   */
  nodeSlugAliases: Record<string, string>;
  docSlugAliases: Record<string, string>;

  /* ---- the documentation workflow (V0.38) ---- */
  /**
   * Doc-page id -> its workflow standing at its RESOLVED locale, from
   * `GET /api/docs/workflows`.
   *
   * SPARSE, and the absence carries meaning: a page with no translation row in ANY of the
   * asked locales is simply not here. The requested → source-locale fallback is resolved
   * SERVER-side now (V0.38): an entry with `fallback: true` says "no current-locale row —
   * you would be reading the source page", which is what lets the pill dots stay honest
   * about which translation they are talking about. `docWorkflowLoaded` distinguishes all
   * of that from "we have not asked" — `myItems.loaded`'s job, for the same reason.
   *
   * Loaded once per locale, gated on being a PARTICIPANT (the endpoint's own gate). The
   * ENTRY OBJECTS are replaced, never mutated, so the store's content compare can use
   * identity on them the way `docTitles`' does on its strings.
   */
  docWorkflow: Record<string, DocWorkflowEntry>;
  docWorkflowLoaded: boolean;
  /**
   * Every account, as `{id, displayName, canBeNodeResponsible, canBeDocResponsible}` — what
   * the responsibles picker offers and what resolves an assignment id to a name. Gated on
   * being a PARTICIPANT (any brick, or the AI operator — the endpoint's own gate); empty and
   * `loaded:false` for everyone else, which is also what a non-participant's map payload
   * implies (it carries no `responsibles` at all).
   */
  userDirectory: DirectoryUser[];
  userDirectoryLoaded: boolean;
  /** `GET /api/users/directory/groups` — same loaded-flag rule as the user directory above. */
  groupDirectory: DirectoryGroup[];
  groupDirectoryLoaded: boolean;

  /* ---- routing (plan 1.6) ---- */
  /**
   * Which admin surface is open, or null. Reported from the open/close actions AND from each
   * region's own `open` effect (`router.reportAdminScreen`, idempotent), because the regions'
   * internal close buttons never go through the actions. The URL's `/admin/{screen}` segment
   * and `currentAppLocation()` read it. The AI-doc Context screen deliberately has no value
   * here — the codec's ADMIN_SCREENS vocabulary has no segment for it.
   */
  adminScreen: AdminScreen | null;
  /**
   * The permissions page's tab (V0.54.5) — the URL's `/admin/permissions/{tab}` segment, read
   * only while `adminScreen === 'permissions'`. Reported by the page, and set by the open action
   * BEFORE the screen is, so opening the page is one history entry rather than two.
   */
  permissionsTab: PermissionTab | null;
  /** The one dismissible banner: which wording to show, or null for none. */
  routeNotice: RouteNoticeKind | null;
  /**
   * A second line the banner appends when the notice can NAME what it is about — which
   * rejected change was lost, for instance. Null when there is nothing more to say, which is
   * every case today: `ConflictReport.rejected` says only `'structural'`, and a delta covering
   * a drag plus two colour changes has no single honest name. The field exists because the
   * wording asks the user to "make the change again", and the moment the write path can say
   * WHICH change that was, the banner should say it too rather than grow a second component.
   */
  noticeDetail: string | null;
  /**
   * The URL the session arrived on, parsed and PARKED until boot can act on it: stashed at
   * the top of `enterApp()`, resolved in the `urlParsed` phase (after permissions), applied
   * after `viewRestored`, then cleared. Never in the snapshot — it is boot's, not a view's.
   */
  pendingRoute: ParsedLocation | null;

  /* ---- my items (plan 2.4) ---- */
  /**
   * This user's favorites and recently-viewed docs, from `GET /api/me/items` — bare
   * references only; labels and visibility are resolved at render time from the map state.
   * `loaded` distinguishes "empty" from "not asked yet / failed", so the pane shows its
   * empty-state hints only when the answer really was "nothing".
   */
  myItems: { favorites: FavoriteRef[]; recents: RecentRef[]; loaded: boolean };

  /**
   * Everything ASSIGNED to this user (V0.38), from `GET /api/me/assignments` — bare refs, the
   * documented `/api/me` pattern: labels, titles and visibility are resolved at render time
   * from the map state, and a ref the current payload cannot resolve is simply not rendered.
   * `via` names the group id when the assignment reaches this user through their team.
   */
  myAssignments: { nodes: AssignedNodeRef[]; docs: AssignedDocRef[]; loaded: boolean };

  /**
   * This user's open to-dos (V0.50), from `GET /api/me/todos`.
   *
   * THE ONE `/api/me` LIST THAT IS NOT BARE REFS, and deliberately so: a to-do's title exists
   * nowhere but its own row, so the house rule ("the client resolves display from map state")
   * would leave a row naming nothing. Visibility is still the server's — a to-do on a hidden
   * branch is never sent, even when it names this user.
   *
   * `todosVersion` is bumped by every to-do write anywhere in the app, and the node popup's
   * section watches it. That is what keeps the pane and an open popup showing the same thing
   * when a row is ticked in one of them: one counter rather than two components trying to
   * remember to tell each other.
   */
  myTodos: { todos: MyTodo[]; loaded: boolean };
  todosVersion: number;

  /**
   * Subtree progress per PROJECT node (V0.50.3) — `{ done, total }` from `GET /api/projects`,
   * rolled up through `lib/projects.ts`, for the map's progress underline in heatmap mode.
   * Loaded for VIEW_TODOS holders only (the to-dos behind it are theirs to see); `{}` for
   * everyone else, whose map draws the mark alone. The mark itself is not here — it rides the
   * map payload as `MapNode.project`. Replaced whole on every load, never mutated.
   */
  projectProgress: Record<string, { done: number; total: number }>;

  /**
   * ONE to-do to open expanded, set just before a popup is opened from somewhere else — the
   * "My items" pane's rows (V0.50.1). Consumed and cleared by the popup's section, so it is a
   * one-shot instruction rather than a selection: nothing stays highlighted afterwards, and a
   * popup opened by any other route is unaffected.
   */
  focusTodoId: string | null;
  /**
   * "Put the cursor in the new-task box" for THIS node (V0.54) — the right-click menu's "Add a
   * task…". `focusTodoId`'s one-shot contract: the popup's Tasks section consumes and clears
   * it, so a popup opened any other way is untouched.
   */
  focusTodoAdd: NodeId | null;

  /**
   * Which foldable SECTIONS this user has shut (V0.50) — the popup's To-dos section and the
   * four "My items" headings, by key. The EXCEPTIONS are stored, never the state, so a section
   * added later defaults to open: see `ViewRecord.collapsedSections`. Replaced on every change,
   * never mutated, so the snapshot adopts it by reference — the `activeTags` contract.
   */
  collapsedSections: Set<string>;
  /**
   * Which of the node popup's foldable SECTIONS this user has OPENED (V0.51), by key.
   *
   * The mirror of `collapsedSections` above, and deliberately not folded into it: the pane's
   * headings default OPEN and store their shut ones, the popup's folds default SHUT and store
   * their opened ones. See `ViewRecord.openSections` for why that is two lists rather than one
   * map. Replaced on every change, never mutated — the `activeTags` contract.
   */
  openSections: Set<string>;

  /* ---- the bell (plan Phase 3) ---- */
  /**
   * What this user watches, as `'node:<id>' | 'doc:<id>'` keys, from
   * `GET /api/me/subscriptions`. REPLACED on every change, never mutated in place, so the
   * snapshot adopts it by reference — the `activeTags` contract. `subscriptionsLoaded`
   * plays `myItems.loaded`'s role: a toggle whose truth is not yet known is not rendered.
   */
  subscriptions: Set<string>;
  subscriptionsLoaded: boolean;
  /**
   * The bell's badge: unread notifications plus unseen changes, from `GET /api/me/feed`.
   * Refreshed on sign-in, window focus and after a doc save — never polled.
   */
  feedUnread: number;

  /* ---- chrome and preferences ---- */
  isLight: boolean;
  sidebarOpen: boolean;
  sidebarWidth: number;
  showLinks: boolean;
  /**
   * The minimap is drawn (V0.53.4). A DURABLE preference beside the theme — on by default, and
   * what this browser last chose survives a new day, unlike `showLinks`.
   */
  minimapOn: boolean;
  /**
   * The minimap stays at its bright level instead of fading when the camera rests (V0.54,
   * the minimap's right-click menu). DURABLE, beside `minimapOn`, for `minimapOn`'s reason.
   */
  minimapBright: boolean;
  editing: boolean;
  /** Ctrl (or Cmd) is held: in edit mode a click adds a child instead of dragging. */
  ctrlAdd: boolean;
  linkArmed: boolean;
  /**
   * The node the right-click menu's "Move to…" is carrying (V0.54): the next node clicked
   * becomes its parent, a click on empty map or Escape puts it down. The Link tool's shape —
   * `linkArmed` — for the other structural verb that needs a second node.
   */
  movingId: NodeId | null;
  /**
   * What the right-click menu's Copy (or Ctrl+C) put on the clipboard (V0.54). It names the
   * SOURCE, not a snapshot: the server copies the node as it is when it is pasted, in every
   * language — which only the server can. Session state; a reload empties it, as it would any
   * clipboard that lives in one tab.
   */
  clipboard: { id: NodeId; branch: boolean; label: string; count: number } | null;
  aiDocMode: boolean;
  /**
   * The clearance heatmap (V0.39): nodes take their bucket colour instead of their own.
   * Edit-mode + participant only (`setHeatmap` self-gates, `setEditing(false)` clears it, as
   * both do for `aiDocMode`), day-scoped in the view record beside `aiDocMode`, and carried
   * in the URL as `?heat=clearance`.
   */
  heatmapOn: boolean;
  /**
   * The documentation rows are PINNED open (V0.42.2): shown at any zoom rather than only
   * past the zoom threshold. One of the two inputs to `docsExpanded` — `evaluateDocsExpand()`
   * is the only place the two are combined. Day-scoped, and switched on by the heatmap.
   */
  docsPinned: boolean;
  /**
   * `GET /api/map/health` — per-node workflow counts for the current locale, SPARSE (a node
   * owning no documentation is absent). CONTAINER-REPLACED by its one writer
   * (`loadMapHealth()`), never mutated, so the snapshot adopts it by reference. `loaded`
   * distinguishes "asked and answered" from "failed / not asked": a failed fetch leaves the
   * mode on over an EMPTY index — the nodes keep their own colours rather than guessing.
   * Deliberately NOT cached against `dataVersion` (doc content does not bump it): refetched
   * on mode-on, after a quiet refresh, and after this tab's own doc save or workflow verb —
   * never on window focus, which would be a poll in disguise.
   */
  mapHealth: { byNode: Record<string, NodeHealthCounts>; loaded: boolean };

  /**
   * Ids of top-bar controls that no longer fit the bar and live in a "…" overflow menu
   * (plan 1.3). ORDERED — first-out first — and REPLACED wholesale by its only writer, the
   * fit controller (`features/topbar/topbarFit.ts`), which is why the snapshot can adopt it
   * by reference. Transient: recomputed from measurement, never persisted.
   */
  topbarOverflow: string[];

  /* ---- filters ---- */
  /**
   * The selected filter tags — SEVERAL at once since plan 2.3 (OR semantics; empty = no
   * filter). REPLACED on every change, never mutated in place, so the snapshot can adopt it
   * by reference and identity still means "this changed" — the same contract `forceOpen`
   * and `chatHighlight` follow.
   */
  activeTags: Set<string>;
  activeDocStatus: string;
  /** The raw text, exactly as typed. `searchQuery` is this trimmed and lowercased. */
  searchText: string;
  searchQuery: string;
  searchScope: SearchScope;
  /**
   * Narrow the search to one documentation kind (plan 2.2), `''` = off. TRANSIENT: starts
   * off on every load and is never persisted — exactly the property the old presence facets
   * had, and for the same reason (a session silently filtered to one kind looks broken).
   */
  searchDocKind: SearchDocKind | '';
  /**
   * Owner-node ids of the documents whose TEXT contains the query (plan 2.2), from
   * `GET /api/search/docs`. On the chatHighlight template: REPLACED on change, never
   * mutated, adopted by the snapshot by reference. `applySearch()` clears it synchronously
   * (the old answer is for the old query); the debounced fetch refills it.
   */
  searchContentHits: Set<string>;
  /** Ids temporarily treated as expanded to reveal a filter hit. Derived, never persisted. */
  forceOpen: Set<string>;
  /** Node ids the last chatbot answer cited — the map's fourth highlight layer. */
  chatHighlight: Set<string>;
  /** The eye assignment filter (V0.42): the FIFTH highlight layer. On, it reveals and
   *  highlights the nodes carrying documents (or nodes) I am responsible for. `assignHighlight`
   *  is the derived id set, recomputed from `myAssignments` when the toggle or the data moves. */
  eyeOn: boolean;
  assignHighlight: Set<string>;

  /* ---- the doc view ---- */
  docCurrent: string;
  docCurrentId: string | null;
  docCurrentTitle: string;
  docNavNodeId: string | null;
  docOpen: boolean;
  /** Bumped on every open, so re-opening the SAME page in the same mode is observable. */
  docOpenSeq: number;
  /** One id per editing session, so repeated saves collapse into a single log entry. */
  docEditSessionId: string | null;
  /** Bumped when a save or a restore adds an entry to the open page's history. */
  docRevision: number;

  /* ---- the node popup ---- */
  /** Which node's popup is open. The presence socket and the Ctrl-hold guard both read it. */
  popupFor: string | null;

  /* ---- the AI documentation suite ---- */
  aiDocCfgCache: GroundingDefaults | null;
  appTargets: AppTarget[];
  /** "No targets exist" and "we have not asked yet" are different sentences in that dropdown. */
  appTargetsLoaded: boolean;

  /* ---- session and deployment ---- */
  currentUser: CurrentUser | null;
  perm: Permissions | null;
  caps: Capabilities | null;
  currentLocale: string;
  languages: Language[];
  hiddenNodeSet: Set<string>;
  hiddenTagSet: Set<string>;

  /* ---- boot ---- */
  /** True once boot has finished asking whether there is a session. */
  booted: boolean;
  bootPhase: BootPhase;
  /**
   * View-state writes are suppressed until boot has had its chance to READ the record.
   *
   * Without it the default-state setup that runs before the restore — `applyPermissions()`
   * calling `setAiDocMode(false)`, `setEditing()` doing the same — would persist an empty view
   * over the one about to be loaded. Reset per session, so a second user signing in on the same
   * browser cannot inherit the first user's "ready".
   */
  /**
   * A page created from a `+` pill and not yet written in (V0.43.2).
   *
   * Transient and deliberately NOT on the snapshot: nothing renders it. It exists so closing a
   * document created by accident can take the page back out, leaving the node owing it again
   * instead of owning an empty one that reads as satisfied. Cleared by the first act that says
   * somebody meant to keep it.
   */
  provisionalDoc: { docId: DocId; nodeId: NodeId } | null;
  viewStateReady: boolean;
}

/** Everything on, `content` included (plan 2.2): doc text is part of an ordinary search. */
export const DEFAULT_SEARCH_SCOPE: SearchScope = {
  name: true,
  desc: true,
  tags: true,
  content: true,
};

/** The one instance. Mutated in place; `publish()` is how anything learns that it was. */
export const S: AppCoreState = {
  nodes: {},
  edges: {},
  links: [],
  tags: {},
  tagEdges: {},
  nid: 0,
  tid: 0,
  dataVersion: 1,

  docTitles: {},
  docAiGenerated: {},
  docTypes: {},
  docUrls: {},
  docSlugs: {},
  docSlugDrift: {},
  nodeSlugAliases: {},
  docSlugAliases: {},

  docWorkflow: {},
  docWorkflowLoaded: false,
  userDirectory: [],
  userDirectoryLoaded: false,
  groupDirectory: [],
  groupDirectoryLoaded: false,

  adminScreen: null,
  permissionsTab: null,
  routeNotice: null,
  mapFiltered: false,
  noticeDetail: null,
  pendingRoute: null,

  myItems: { favorites: [], recents: [], loaded: false },
  myAssignments: { nodes: [], docs: [], loaded: false },
  myTodos: { todos: [], loaded: false },
  todosVersion: 0,
  projectProgress: {},
  focusTodoId: null,
  focusTodoAdd: null,
  collapsedSections: new Set<string>(),
  openSections: new Set<string>(),

  subscriptions: new Set(),
  subscriptionsLoaded: false,
  feedUnread: 0,

  isLight: false,
  sidebarOpen: true,
  sidebarWidth: 260,
  // OFF at the start of every session, and not persisted at all — see store/persistence.ts.
  showLinks: false,
  minimapOn: true,
  minimapBright: false,
  editing: false,
  ctrlAdd: false,
  linkArmed: false,
  movingId: null,
  clipboard: null,
  aiDocMode: false,
  heatmapOn: false,
  docsPinned: false,
  mapHealth: { byNode: {}, loaded: false },

  topbarOverflow: [],

  activeTags: new Set(),
  activeDocStatus: '',
  searchText: '',
  searchQuery: '',
  searchScope: { ...DEFAULT_SEARCH_SCOPE },
  searchDocKind: '',
  searchContentHits: new Set(),
  forceOpen: new Set(),
  chatHighlight: new Set(),
  eyeOn: false,
  assignHighlight: new Set(),

  docCurrent: '',
  docCurrentId: null,
  docCurrentTitle: '',
  docNavNodeId: null,
  docOpen: false,
  docOpenSeq: 0,
  docEditSessionId: null,
  docRevision: 0,

  popupFor: null,

  aiDocCfgCache: null,
  appTargets: [],
  appTargetsLoaded: false,

  currentUser: null,
  perm: null,
  caps: null,
  currentLocale: 'en',
  languages: [],
  hiddenNodeSet: new Set(),
  hiddenTagSet: new Set(),

  booted: false,
  bootPhase: 'booting',
  provisionalDoc: null,
  viewStateReady: false,
};

/* --------------------------------------------------------------------- the read model */

/**
 * What every component reads.
 *
 * Flat, cached, and replaced whole on each publish. It is deliberately NOT `AppCoreState`:
 * several fields here are derived (`undoLabel`, the presence roster) and several of the core's
 * are private to the app (the counters, the hidden sets, the dirty bookkeeping).
 */
export interface AppSnapshot {
  nodes: NodeMap;
  edges: Edges;
  tags: TagMap;
  tagEdges: TagEdges;
  /** Dotted cross-links. Map content, so it belongs here rather than being special-cased. */
  links: readonly CrossLink[];
  perm: Permissions | null;

  /**
   * True once boot has finished asking whether there is a session.
   *
   * Without it a region cannot tell "not signed in" from "not asked yet", and the login
   * overlay would flash for an already-signed-in user while the check was in flight.
   */
  booted: boolean;
  /** Null when signed out. One fact, so nothing can disagree with it. */
  currentUser: CurrentUser | null;

  showLinks: boolean;
  linkArmed: boolean;
  ctrlAdd: boolean;
  aiDocMode: boolean;
  /** The clearance heatmap (V0.39). See `AppCoreState` — the toggle and the legend read it. */
  heatmapOn: boolean;
  /** The documentation rows are pinned open (V0.42.2) — the corner toggle's own state. */
  docsPinned: boolean;
  /** Per-node workflow counts for the heatmap. Adopted by reference; replaced on every load. */
  mapHealth: { readonly byNode: Record<string, NodeHealthCounts>; readonly loaded: boolean };
  /**
   * What the next undo would undo, or null when there is nothing.
   *
   * A label rather than the stack, because "can undo" and "undo what" are the same question
   * for every consumer, and handing over the stack would invite reading past the top of it.
   */
  undoLabel: string | null;
  /** What this DEPLOYMENT can do, from /api/capabilities. Null means "not asked / unknown". */
  caps: Capabilities | null;

  /**
   * Doc-page id -> title, resolved server-side for the current locale.
   *
   * A page's title is not on the node: one node can point at several pages, and a title is
   * per-language while the node is not.
   */
  docTitles: Record<string, string>;
  /** Doc-page id -> the URL that opens it. Derived on load; never persisted. */
  docUrls: Record<string, string>;
  /** Doc-page id -> its type, for the pages that are not `standard`. */
  docTypes: Record<string, DocType>;
  /** See `S.docSlugDrift` — sparse, and decided by the server rather than here. */
  docSlugDrift: Record<string, true>;
  /** Doc-page id -> true when Claude wrote it. AI-admin-only, from /api/map. */
  docAiGenerated: Record<string, boolean>;

  /**
   * Doc-page id -> workflow standing at its RESOLVED locale (V0.38). Sparse: an absent id
   * means "no translation row anywhere we asked"; `fallback: true` on an entry means "no
   * current-locale row". `docWorkflowLoaded` tells all of that apart from "not asked yet".
   * Participants only. See `AppCoreState` for the full note.
   */
  docWorkflow: Record<string, DocWorkflowEntry>;
  docWorkflowLoaded: boolean;
  /** Every account with its two eligibility flags, for the responsibles picker. Participants only. */
  userDirectory: readonly DirectoryUser[];
  userDirectoryLoaded: boolean;
  /** Every group with its members and eligibility, for the same picker (V0.37.1). */
  groupDirectory: readonly DirectoryGroup[];
  groupDirectoryLoaded: boolean;

  /**
   * This user's favorites and recently-viewed docs (plan 2.4). Bare references; the
   * "My items" pane and the star toggles resolve display and visibility from the rest of
   * the snapshot at render time.
   */
  myItems: {
    favorites: readonly FavoriteRef[];
    recents: readonly RecentRef[];
    loaded: boolean;
  };

  /** Everything assigned to this user (V0.38) — bare refs, resolved at render time. */
  myAssignments: {
    nodes: readonly AssignedNodeRef[];
    docs: readonly AssignedDocRef[];
    loaded: boolean;
  };

  /** This user's open to-dos (V0.50) — rows, not refs. See the mutable declaration for why. */
  myTodos: { todos: readonly MyTodo[]; loaded: boolean };
  /** Bumped by every to-do write; the popup's section re-reads its node's list when it moves. */
  todosVersion: number;
  /** Per project node, its subtree's to-dos done / total — the heatmap underline's figures (V0.50.3). */
  projectProgress: Readonly<Record<string, { done: number; total: number }>>;
  /** A one-shot "open this to-do expanded", consumed by the popup's section. */
  focusTodoId: string | null;
  /** A one-shot "focus the new-task box" for this node (V0.54), consumed by the same section. */
  focusTodoAdd: NodeId | null;
  /** Which foldable sections this user has shut. Adopted by reference; replaced on change. */
  collapsedSections: ReadonlySet<string>;
  /** Which of the popup's folds this user has OPENED (V0.51). Same contract, opposite default. */
  openSections: ReadonlySet<string>;

  /**
   * What this user watches (plan Phase 3), as `'node:<id>' | 'doc:<id>'` keys. Adopted by
   * reference; replaced on every change. `subscriptionsLoaded` gates the toggles the same
   * way `myItems.loaded` gates the stars.
   */
  subscriptions: ReadonlySet<string>;
  subscriptionsLoaded: boolean;
  /** The bell's badge count. */
  feedUnread: number;

  /**
   * Who else is connected, one entry per SOCKET.
   *
   * Not per person: somebody with two tabs open appears twice, and collapsing that is the
   * consumer's job because which of their tabs to show depends on what is being shown.
   */
  presenceRoster: readonly PresenceUser[];

  docOpen: boolean;
  docCurrentId: string | null;
  docCurrent: string;
  docCurrentTitle: string;
  docNavNodeId: string | null;
  docOpenSeq: number;
  docEditSessionId: string | null;
  docRevision: number;
  languages: readonly Language[];

  /** Which admin surface is open (plan 1.6) — the URL's `/admin/{screen}` segment. */
  adminScreen: AdminScreen | null;
  /** The dismissible banner's wording, or null for none (plans 1.6 and Part 3). */
  routeNotice: RouteNoticeKind | null;
  /** Were nodes withheld from the map payload (V0.43)? See `AppCoreState`. */
  mapFiltered: boolean;
  /** A second line naming what the notice is about, or null. See `AppCoreState`. */
  noticeDetail: string | null;

  /**
   * The two AI caches the node popup reads.
   *
   * `aiDocCfgCache` is the suite-wide grounding config: the popup labels each per-source
   * "Inherit" option with what inheriting resolves to RIGHT NOW, which is why it is cached
   * rather than fetched per open. Null means "assume the server's defaults".
   */
  aiDocCfgCache: GroundingDefaults | null;
  appTargets: readonly AppTarget[];
  appTargetsLoaded: boolean;

  editing: boolean;
  isLight: boolean;
  sidebarOpen: boolean;
  /** The minimap is drawn (V0.53.4). The card and the zoom-stack button both read it. */
  minimapOn: boolean;
  /** The minimap does not fade when the camera rests (V0.54). */
  minimapBright: boolean;
  /** What Copy put on the clipboard (V0.54) — the right-click menu's Paste reads it. */
  clipboard: { id: NodeId; branch: boolean; label: string; count: number } | null;
  /**
   * Which top-bar controls currently live in a "…" overflow menu instead of the bar (plan
   * 1.3). Adopted by reference, not copied: the fit controller replaces the array wholesale
   * on every change, so identity carries "this changed" the same way `forceOpen`'s does.
   */
  topbarOverflow: readonly string[];
  /** The selected filter tags (plan 2.3). Adopted by reference; replaced on every change. */
  activeTags: ReadonlySet<string>;
  /** Already lowercased and trimmed. Every filter matches against THIS; nothing renders it. */
  searchQuery: string;
  /**
   * The raw text, exactly as typed.
   *
   * Separate from `searchQuery` because the search box is a controlled input. Binding it to the
   * normalised form would rewrite what somebody typed — "Ab" would appear as "ab" — and move
   * their caret on every keystroke.
   */
  searchText: string;
  searchScope: SearchScope;
  /** The doc-kind narrowing (plan 2.2), '' = off. Transient; never persisted. */
  searchDocKind: SearchDocKind | '';
  /** Owner nodes of doc-content hits (plan 2.2). Adopted by reference; replaced on change. */
  searchContentHits: ReadonlySet<string>;
  currentLocale: string;
  forceOpen: ReadonlySet<string>;
  /** The pipeline-stage filter (AI-doc mode's toolbar), or `''` for "any". */
  activeDocStatus: PipelineStage | '';
  chatHighlight: ReadonlySet<string>;
  eyeOn: boolean;
  assignHighlight: ReadonlySet<string>;

  /**
   * How far through boot the app has got.
   *
   * A VALUE, not an event. `store/slices/boot.ts` turns CLAUDE.md's three view-state ordering
   * rules into a data invariant, and it can only do that if it is told where boot is rather
   * than guessing from what the snapshot happens to contain.
   */
  bootPhase: BootPhase;
}

let cached: AppSnapshot | null = null;
const listeners = new Set<() => void>();

function build(): AppSnapshot {
  return {
    // Fresh CONTAINERS, shared ELEMENTS — see the note at the top of this file.
    nodes: { ...S.nodes },
    edges: { ...S.edges },
    tags: { ...S.tags },
    tagEdges: { ...S.tagEdges },
    links: [...S.links],
    perm: S.perm,
    booted: S.booted,
    currentUser: S.currentUser,
    showLinks: S.showLinks,
    linkArmed: S.linkArmed,
    ctrlAdd: S.ctrlAdd,
    aiDocMode: S.aiDocMode,
    heatmapOn: S.heatmapOn,
    docsPinned: S.docsPinned,
    mapHealth: S.mapHealth,
    undoLabel: undoLabel(),
    caps: S.caps,
    docTitles: { ...S.docTitles },
    docUrls: { ...S.docUrls },
    docTypes: { ...S.docTypes },
    docSlugDrift: { ...S.docSlugDrift },
    docAiGenerated: { ...S.docAiGenerated },
    // BY REFERENCE, like `mapHealth` and the directories (V0.44.1). Every writer replaces the
    // container (`loadDocWorkflows`, `setDocResponsibles`, `noteDocWorkflow`), so identity means
    // "the workflow map moved" — which is what the dashboard's fetch effect keys on. Spreading it
    // here gave it a fresh identity on EVERY publish, and the Documents tab refetched on every
    // render, doc open and presence tick.
    docWorkflow: S.docWorkflow,
    docWorkflowLoaded: S.docWorkflowLoaded,
    userDirectory: S.userDirectory,
    userDirectoryLoaded: S.userDirectoryLoaded,
    groupDirectory: S.groupDirectory,
    groupDirectoryLoaded: S.groupDirectoryLoaded,
    myItems: {
      favorites: [...S.myItems.favorites],
      recents: [...S.myItems.recents],
      loaded: S.myItems.loaded,
    },
    myAssignments: {
      nodes: [...S.myAssignments.nodes],
      docs: [...S.myAssignments.docs],
      loaded: S.myAssignments.loaded,
    },
    myTodos: { todos: [...S.myTodos.todos], loaded: S.myTodos.loaded },
    todosVersion: S.todosVersion,
    projectProgress: S.projectProgress,
    focusTodoId: S.focusTodoId,
    focusTodoAdd: S.focusTodoAdd,
    collapsedSections: S.collapsedSections,
    openSections: S.openSections,
    subscriptions: S.subscriptions,
    subscriptionsLoaded: S.subscriptionsLoaded,
    feedUnread: S.feedUnread,
    presenceRoster: [...getRoster()],
    docOpen: S.docOpen,
    docCurrentId: S.docCurrentId,
    docCurrent: S.docCurrent,
    docCurrentTitle: S.docCurrentTitle,
    docNavNodeId: S.docNavNodeId,
    docOpenSeq: S.docOpenSeq,
    docEditSessionId: S.docEditSessionId,
    docRevision: S.docRevision,
    languages: S.languages,
    adminScreen: S.adminScreen,
    routeNotice: S.routeNotice,
    mapFiltered: S.mapFiltered,
    noticeDetail: S.noticeDetail,
    aiDocCfgCache: S.aiDocCfgCache,
    appTargets: [...S.appTargets],
    appTargetsLoaded: S.appTargetsLoaded,
    editing: S.editing,
    isLight: S.isLight,
    sidebarOpen: S.sidebarOpen,
    minimapOn: S.minimapOn,
    minimapBright: S.minimapBright,
    clipboard: S.clipboard,
    topbarOverflow: S.topbarOverflow,
    activeTags: S.activeTags,
    searchQuery: S.searchQuery,
    searchText: S.searchText,
    searchScope: S.searchScope,
    searchDocKind: S.searchDocKind,
    searchContentHits: S.searchContentHits,
    currentLocale: S.currentLocale,
    forceOpen: S.forceOpen,
    activeDocStatus: S.activeDocStatus as PipelineStage | '',
    chatHighlight: S.chatHighlight,
    eyeOn: S.eyeOn,
    assignHighlight: S.assignHighlight,
    bootPhase: S.bootPhase,
  };
}

/**
 * Say the state moved.
 *
 * Invalidates the snapshot, mirrors it into the Zustand store, and notifies subscribers — in
 * that order, so a component reading both sees a store that already agrees with the snapshot it
 * is about to be handed rather than rendering once against each.
 */
export function publish(): void {
  cached = null;
  mirrorAppSnapshot(getSnapshot());
  for (const l of listeners) l();
}

export function getSnapshot(): AppSnapshot {
  if (!cached) cached = build();
  return cached;
}

/**
 * A snapshot of the empty app: no map, signed out, nothing filtered.
 *
 * What the state looks like before boot has answered anything, and the base a unit test builds
 * a case on top of. Frozen so a test that writes to it fails loudly rather than corrupting a
 * shared object for every other test in the file.
 */
export const EMPTY: AppSnapshot = Object.freeze({
  nodes: Object.freeze({}) as NodeMap,
  edges: Object.freeze({}) as Edges,
  tags: Object.freeze({}) as TagMap,
  tagEdges: Object.freeze({}) as TagEdges,
  links: Object.freeze([]) as readonly CrossLink[],
  mapFiltered: false,
  perm: null,
  booted: false,
  currentUser: null,
  showLinks: false,
  linkArmed: false,
  ctrlAdd: false,
  aiDocMode: false,
  heatmapOn: false,
  docsPinned: false,
  mapHealth: Object.freeze({
    byNode: Object.freeze({}) as Record<string, NodeHealthCounts>,
    loaded: false,
  }),
  undoLabel: null,
  caps: null,
  docTitles: Object.freeze({}) as Record<string, string>,
  docSlugDrift: Object.freeze({}) as Record<string, true>,
  docUrls: Object.freeze({}) as Record<string, string>,
  docTypes: Object.freeze({}) as Record<string, DocType>,
  docAiGenerated: Object.freeze({}) as Record<string, boolean>,
  docWorkflow: Object.freeze({}) as Record<string, DocWorkflowEntry>,
  docWorkflowLoaded: false,
  userDirectory: Object.freeze([]) as readonly DirectoryUser[],
  userDirectoryLoaded: false,
  groupDirectory: Object.freeze([]) as readonly DirectoryGroup[],
  groupDirectoryLoaded: false,
  myItems: Object.freeze({
    favorites: Object.freeze([]) as readonly FavoriteRef[],
    recents: Object.freeze([]) as readonly RecentRef[],
    loaded: false,
  }),
  myAssignments: Object.freeze({
    nodes: Object.freeze([]) as readonly AssignedNodeRef[],
    docs: Object.freeze([]) as readonly AssignedDocRef[],
    loaded: false,
  }),
  myTodos: Object.freeze({ todos: Object.freeze([]) as readonly MyTodo[], loaded: false }),
  todosVersion: 0,
  projectProgress: Object.freeze({}),
  focusTodoId: null,
  focusTodoAdd: null,
  collapsedSections: new Set<string>(),
  openSections: new Set<string>(),
  subscriptions: new Set<string>(),
  subscriptionsLoaded: false,
  feedUnread: 0,
  presenceRoster: Object.freeze([]) as readonly PresenceUser[],
  docOpen: false,
  docCurrentId: null,
  docCurrent: '',
  docCurrentTitle: '',
  docNavNodeId: null,
  docOpenSeq: 0,
  docEditSessionId: null,
  docRevision: 0,
  languages: Object.freeze([]) as readonly Language[],
  adminScreen: null,
  routeNotice: null,
  noticeDetail: null,
  aiDocCfgCache: null,
  appTargets: Object.freeze([]) as readonly AppTarget[],
  appTargetsLoaded: false,
  editing: false,
  isLight: false,
  sidebarOpen: true,
  minimapOn: true,
  minimapBright: false,
  clipboard: null,
  topbarOverflow: Object.freeze([]) as readonly string[],
  activeTags: new Set<string>(),
  searchQuery: '',
  searchText: '',
  searchScope: Object.freeze({ ...DEFAULT_SEARCH_SCOPE }) as SearchScope,
  searchDocKind: '' as SearchDocKind | '',
  searchContentHits: new Set<string>(),
  currentLocale: 'en',
  forceOpen: new Set<string>(),
  activeDocStatus: '' as PipelineStage | '',
  chatHighlight: new Set<string>(),
  eyeOn: false,
  assignHighlight: new Set<string>(),
  bootPhase: 'booting' as BootPhase,
});

/**
 * THE ANNOUNCEMENT POINT, and the app's external handle.
 *
 * `window.InfoStationReact` is not this migration's invention and does not go with it: it is
 * how the surrounding CONIGON suite has asked this app to open a document, change language or
 * re-render, and how an embedding page observes that something changed. `pageScope.ts`
 * publishes this exact object, so what an embedder replaces is what the app calls.
 *
 * That is why `announce()` goes through the property rather than calling `publish()` directly.
 * The incumbent did the same — every one of its ~50 notification sites said
 * `window.InfoStationReact?.sync?.()` — and it is what makes "wrap sync and you see every
 * state change" true rather than nearly true.
 */
export const pageHandle: Record<string, unknown> = { sync: publish };

/** "The state moved, show it." One route, so an observer of it misses nothing. */
export function announce(): void {
  (pageHandle.sync as () => void)();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Read the app state from React, re-rendering when it changes.
 *
 * `useSyncExternalStore` is the right primitive rather than a context: the state lives outside
 * React entirely, and this is the hook React provides for exactly that. It also handles the
 * tearing case a naive subscribe-and-setState would not.
 */
export function useAppState(): AppSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Read one derived value, re-rendering only when THAT value changes.
 *
 * The selector's result is compared by identity, so it must return a primitive or a stable
 * reference: `s => s.editing` is fine, `s => Object.keys(s.nodes)` re-renders every time and is
 * a bug.
 */
export function useAppSelector<T>(selector: (s: AppSnapshot) => T): T {
  return useSyncExternalStore(
    subscribe,
    () => selector(getSnapshot()),
    () => selector(getSnapshot()),
  );
}

/** A node, or undefined. Sugar for the many call sites that guard on it. */
export const nodeOf = (id: string | null | undefined): MapNode | undefined =>
  id ? S.nodes[id] : undefined;
